import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { server } from '../api/service/server/axios';

// Only the refresh token is persisted to localStorage.
// The access token lives in React state (memory) only — never written to storage.
const REFRESH_KEY = 'olimpica_refresh_token';
const ApiAuthContext = createContext(null);

const authBase = () => server();

const decodeProfile = (accessToken) => {
  try {
    const decoded = jwtDecode(accessToken);
    return {
      given_name: decoded.given_name || decoded.preferred_username || decoded.name || 'Usuario',
      preferred_username: decoded.preferred_username || '',
      email: decoded.email || '',
      sub: decoded.sub,
    };
  } catch {
    return { given_name: 'U', preferred_username: '' };
  }
};

export function ApiAuthProvider({ children }) {
  // access_token lives here in memory — never in localStorage
  const [accessToken, setAccessToken] = useState(null);
  const [expiresIn, setExpiresIn] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  const saveSession = useCallback((data) => {
    // access_token → memory only
    setAccessToken(data.access_token);
    setExpiresIn(data.expires_in || null);
    // refresh_token → localStorage (for session persistence across reloads)
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_KEY, data.refresh_token);
    }
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setExpiresIn(null);
    localStorage.removeItem(REFRESH_KEY);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return null;
    try {
      const res = await axios.post(`${authBase()}/auth/refresh`, { refresh_token: refreshToken });
      saveSession(res.data);
      return res.data;
    } catch {
      clearSession();
      return null;
    }
  }, [saveSession, clearSession]);

  // On mount: if a refresh token exists, silently restore the session
  useEffect(() => {
    const storedRefresh = localStorage.getItem(REFRESH_KEY);
    if (storedRefresh && !accessToken) {
      setIsRefreshing(true);
      refresh().finally(() => setIsRefreshing(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh 60 s before token expiry
  useEffect(() => {
    if (!expiresIn || !localStorage.getItem(REFRESH_KEY)) return;
    const delay = Math.max((expiresIn - 60) * 1000, 5000);
    refreshTimerRef.current = setTimeout(refresh, delay);
    return () => clearTimeout(refreshTimerRef.current);
  }, [expiresIn, refresh]);

  const login = async (username, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${authBase()}/auth/login`, { username, password });
      saveSession(res.data);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Usuario o contraseña incorrectos';
      setError(Array.isArray(msg) ? msg.join(', ') : String(msg));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (refreshToken) {
      try {
        await axios.post(`${authBase()}/auth/logout`, { refresh_token: refreshToken });
      } catch {}
    }
    clearSession();
  }, [clearSession]);

  const isAuthenticated = !!accessToken;

  const user = accessToken
    ? {
        access_token: accessToken,
        profile: decodeProfile(accessToken),
      }
    : null;

  return (
    <ApiAuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading: isLoading || isRefreshing,
        error,
        login,
        logout,
        refresh,
        setError,
      }}
    >
      {children}
    </ApiAuthContext.Provider>
  );
}

export function useApiAuth() {
  const ctx = useContext(ApiAuthContext);
  if (!ctx) throw new Error('useApiAuth debe usarse dentro de ApiAuthProvider');
  return ctx;
}
